import moment from "moment";
import type { SchoolClass, StudentTestContentWrapper } from "p6m-p6u";
import CreateEditTextInputPopup from "../../basic/popups/CreateEditTextInputPopup";
import ToggleSwitch from "../../basic/toggleSwitch/ToggleSwitch";
import { FormattedText } from "../teacherArea/FormattedText";
import DeleteConfirmationPopup from "../../basic/popups/DeleteConfirmationPopup";
import DownloadPdfPopup from "../../basic/popups/DownloadPdfPopup";
import EditTestDetailsPopup from "../../basic/popups/EditTestDetailsPopup";
import ShareActivityLinkPopup, { ShareActivityTabs } from "../../basic/popups/ShareActivityLinkPopup";
import ShareFlowPopup from "../shareFlow/ShareFlowPopup";
import { buildTestShareFlowPopupProps, buildVocabShareFlowPopupProps } from "../shareFlow/shareFlowPopupProps";
import InviteClassMembersPopup from "../shareFlow/InviteClassMembersPopup";
import ToastMessage from "../../basic/toastMessage/ToastMessage";
import EditActivityVisibilityModal from "../activityVisibility/EditActivityVisibilityModal";
import { ButtonColorVariant } from "../teacherArea/AreaButton";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { MAX_NAME_LENGTH } from "../../constants/LimitConstants";
import { ShareModes } from "../../enums/shareFlow";
import { getStudentTestUrl } from "../../helpers/ClassActivityMapping";
import {
    ActivityVisibility,
    resolveTestActivityVisibility,
    resolveVocabActivityVisibility,
    toVisibilityDate,
    visibilityToMs,
} from "../../helpers/ActivityVisibility";
import type { useVocabTrainingOperations } from "../../hooks/useVocabTrainingOperations";
import { TestPopupActions, type useTestOperations } from "../../hooks/useTestOperations";
import { toClassVisibilityProp } from "../../helpers/analyticsEventProps";
import { useLogger } from "../../hooks/useLogger";

export interface ClassActivityPopupsTranslations {
    deleteVocabTraining: string;
    deleteVocabTrainingConfirmation: string;
    deleteTask: string;
    deleteTaskConfirmation: (taskName: string) => string;
    deleteConfirmationNote: string;
    duplicateTask: string;
    duplicateVocabTraining: string;
    duplicate: string;
    taskName: string;
    vocabTrainingName: string;
    copyOf: (name: string) => string;
    assignCopyToClass: (schoolClassName: string) => string;
    assignCopyToSameClass: string;
}

/**
 * The useTestOperations and useVocabTrainingOperations hooks are passed as props (not called directly) because ClassActivityList and ClassActivityPopups
 * share the same state instances (e.g. testPopup, vocabTrainingDataToDelete).
 * Calling the hooks separately would create independent state that doesn't stay in sync.
 */
interface ClassActivityPopupsProps {
    testOps: ReturnType<typeof useTestOperations>;
    vocabTrainingOps: ReturnType<typeof useVocabTrainingOperations>;
    translations: ClassActivityPopupsTranslations;
    schoolClasses: SchoolClass[];
    studentTestContentWrapper: StudentTestContentWrapper;
}

export const ClassActivityPopups = (props: ClassActivityPopupsProps) => {
    const { logEvent } = useLogger();

    const sharedTest = props.testOps.userTests.find((t) => t.id === props.testOps.shareLinkPopupTestId);
    const testClassCode = sharedTest?.groupId;
    const sharedVocabTraining = props.vocabTrainingOps.shareLinkPopupTraining;
    const vocabTrainingClassCode = sharedVocabTraining?.groupId;

    // The share popup's "Class" tab copies a classroom link, not an activity link — that is class
    // access being shared, so it must not report an activity-link copy. No members_count here: this
    // surface never loads the member list, and a fabricated 0 would be worse than an absent property.
    // class_visibility follows the same rule: omitted when the class isn't in the loaded list.
    const handleClassLinkCopied = (classCode: string | undefined) => {
        if (!classCode) return;
        const schoolClass = props.schoolClasses.find((c) => c.token === classCode);
        logEvent("shared class access", {
            class_id: classCode,
            method: "link",
            ...(schoolClass ? { class_visibility: toClassVisibilityProp(schoolClass.classVisibility) } : {}),
        });
    };

    const editTestVisibility = props.testOps.editActivityVisibilityPopup;
    const editVocabVisibility = props.vocabTrainingOps.editActivityVisibilityPopup;

    const editTestResult = editTestVisibility
        ? resolveTestActivityVisibility(props.studentTestContentWrapper[editTestVisibility.testId]?.visibilityDate)
        : null;
    const editVocabResult = editVocabVisibility
        ? resolveVocabActivityVisibility(
              props.vocabTrainingOps.vocabMetadata[editVocabVisibility.trainingId]?.visibilityDate
          )
        : null;

    const editTestDetailsTestId = props.testOps.editTestDetails.testId;
    const editTestDetailsVisibilityDateMs = editTestDetailsTestId
        ? props.studentTestContentWrapper[editTestDetailsTestId]?.visibilityDate
        : undefined;
    const editTestDetailsVisibilityDate =
        editTestDetailsVisibilityDateMs != null ? moment(editTestDetailsVisibilityDateMs) : null;

    const editTestVisibilityDueDateMs = editTestVisibility
        ? props.studentTestContentWrapper[editTestVisibility.testId]?.dueDate
        : undefined;
    const editTestVisibilityDeadline = editTestVisibilityDueDateMs ? moment(editTestVisibilityDueDateMs) : null;

    // Practices never store answers, so the "answers and evaluations will be deleted" note only applies to
    // shared tests. Legacy shares predate shareMode and are always tests.
    const deleteTestShare = props.testOps.testPopup?.id
        ? props.studentTestContentWrapper[props.testOps.testPopup.id]
        : undefined;
    const showDeleteAnswersNote = !!deleteTestShare && deleteTestShare.shareMode !== ShareModes.PRACTICE;
    const deleteTaskConfirmation = props.translations.deleteTaskConfirmation(props.testOps.testPopup?.name ?? "");
    const deleteTaskDescription = showDeleteAnswersNote
        ? `${deleteTaskConfirmation}<br/>${props.translations.deleteConfirmationNote}`
        : deleteTaskConfirmation;

    // Mutually exclusive in practice; vocab-first precedence is a defensive fallback.
    const activeInviteModal = props.vocabTrainingOps.inviteModal
        ? {
              state: props.vocabTrainingOps.inviteModal,
              onLater: props.vocabTrainingOps.handleCloseInviteModal,
              onInviteNow: props.vocabTrainingOps.handleInviteNavigate,
              category: ClassActivityCategory.VOCAB_TRAINING,
          }
        : props.testOps.inviteModal
        ? {
              state: props.testOps.inviteModal,
              onLater: props.testOps.handleCloseInviteModal,
              onInviteNow: props.testOps.handleInviteNavigate,
              category: ClassActivityCategory.TEST,
          }
        : null;

    const renderKeepClassToggle = (
        testId: string,
        schoolClassId: string | undefined,
        checked: boolean,
        onChange: () => void
    ) => {
        if (!schoolClassId) return null;

        const schoolClassName = props.schoolClasses.find((c) => c.token === schoolClassId)?.name;
        const toggleText = schoolClassName
            ? props.translations.assignCopyToClass(schoolClassName)
            : props.translations.assignCopyToSameClass;

        return (
            <label
                className="flex items-center gap-s w-fit cursor-pointer"
                data-testid={testId}
            >
                <span className="shrink-0">
                    <ToggleSwitch
                        checked={checked}
                        onChange={onChange}
                    />
                </span>
                <FormattedText
                    rawText={toggleText}
                    textColor="text-l text-p6-grey-dark [&>b]:font-semibold"
                />
            </label>
        );
    };

    return (
        <>
            {props.testOps.downloadPopupTestDetails && (
                <DownloadPdfPopup
                    isOpen={!!props.testOps.downloadPopupTestDetails}
                    onClose={() => props.testOps.setDownloadPopupTestDetails(null)}
                    testDetails={props.testOps.downloadPopupTestDetails}
                />
            )}

            <DeleteConfirmationPopup
                isOpen={props.vocabTrainingOps.vocabTrainingDataToDelete !== null}
                title={props.translations.deleteVocabTraining}
                description={props.translations.deleteVocabTrainingConfirmation}
                onClose={() => props.vocabTrainingOps.setVocabTrainingDataToDelete(null)}
                onConfirm={props.vocabTrainingOps.handleConfirmDeleteVocabTraining}
                primaryButtonColor={ButtonColorVariant.SECONDARY}
            />

            <DeleteConfirmationPopup
                isOpen={props.testOps.testPopup?.action === TestPopupActions.DELETE}
                title={props.translations.deleteTask}
                description={deleteTaskDescription}
                onClose={() => props.testOps.setTestPopup(null)}
                onConfirm={props.testOps.handleConfirmDeleteTest}
            />

            <ShareActivityLinkPopup
                isOpen={!!props.testOps.shareLinkPopupTestId}
                onClose={props.testOps.handleCloseShareLinkPopup}
                activityLink={
                    props.testOps.shareLinkPopupTestId ? getStudentTestUrl(props.testOps.shareLinkPopupTestId) : ""
                }
                schoolClassId={sharedTest?.groupId}
                category={ClassActivityCategory.TEST}
                shareMode={sharedTest?.shareMode}
                onLinkCopied={(linkTarget) =>
                    linkTarget === ShareActivityTabs.CLASS
                        ? handleClassLinkCopied(testClassCode)
                        : props.testOps.handleTestLinkCopied(props.testOps.shareLinkPopupTestId)
                }
            />

            <ShareActivityLinkPopup
                isOpen={!!sharedVocabTraining}
                onClose={props.vocabTrainingOps.handleCloseShareLinkPopup}
                activityLink={sharedVocabTraining?.shareLink ?? ""}
                schoolClassId={sharedVocabTraining?.groupId}
                category={ClassActivityCategory.VOCAB_TRAINING}
                onLinkCopied={(linkTarget) =>
                    linkTarget === ShareActivityTabs.CLASS
                        ? handleClassLinkCopied(vocabTrainingClassCode)
                        : props.vocabTrainingOps.handleVocabTrainingLinkCopied(sharedVocabTraining)
                }
            />

            <ShareFlowPopup {...buildTestShareFlowPopupProps(props.testOps, props.schoolClasses)} />

            <ShareFlowPopup {...buildVocabShareFlowPopupProps(props.vocabTrainingOps, props.schoolClasses)} />

            {activeInviteModal && (
                <InviteClassMembersPopup
                    isOpen
                    schoolClassName={activeInviteModal.state.schoolClassName}
                    onLater={activeInviteModal.onLater}
                    onInviteNow={activeInviteModal.onInviteNow}
                    category={activeInviteModal.category}
                />
            )}

            {!!props.testOps.editTestDetails.testId && (
                <EditTestDetailsPopup
                    isOpen
                    onClose={props.testOps.handleCloseEditTestDetails}
                    onSave={props.testOps.handleSaveEditTestDetails}
                    isSaving={props.testOps.editTestDetailsInProgress}
                    selectedMode={props.testOps.editTestDetails.shareMode}
                    message={props.testOps.editTestDetails.message}
                    showDeadline={props.testOps.editTestDetails.deadline.show}
                    deadlineValue={props.testOps.editTestDetails.deadline.value}
                    visibilityDate={editTestDetailsVisibilityDate}
                    showDuration={props.testOps.editTestDetails.duration.show}
                    durationHours={props.testOps.editTestDetails.duration.hours.display}
                    durationMinutes={props.testOps.editTestDetails.duration.minutes.display}
                    handlers={props.testOps.editDetailHandlers}
                />
            )}

            <CreateEditTextInputPopup
                isOpen={props.testOps.testPopup?.action === TestPopupActions.DUPLICATE}
                onClose={() => props.testOps.setTestPopup(null)}
                title={props.translations.duplicateTask}
                label={props.translations.taskName}
                initialValue={props.translations.copyOf(props.testOps.testPopup?.name ?? "")}
                primaryButton={{
                    text: props.translations.duplicate,
                    onSubmit: props.testOps.handleConfirmDuplicateTest,
                }}
            >
                {renderKeepClassToggle(
                    "duplicate-test-keep-class-toggle",
                    props.testOps.testPopup?.schoolClassId,
                    props.testOps.keepClassOnDuplicate,
                    props.testOps.toggleKeepClassOnDuplicate
                )}
            </CreateEditTextInputPopup>

            <CreateEditTextInputPopup
                isOpen={!!props.vocabTrainingOps.duplicatePopup}
                onClose={props.vocabTrainingOps.handleCloseDuplicateVocabTrainingPopup}
                title={props.translations.duplicateVocabTraining}
                label={props.translations.vocabTrainingName}
                initialValue={props.translations
                    .copyOf(props.vocabTrainingOps.duplicatePopup?.name ?? "")
                    .slice(0, MAX_NAME_LENGTH)}
                maxLength={MAX_NAME_LENGTH}
                primaryButton={{
                    text: props.translations.duplicate,
                    onSubmit: props.vocabTrainingOps.handleConfirmDuplicateVocabTraining,
                }}
            >
                {renderKeepClassToggle(
                    "duplicate-vocab-training-keep-class-toggle",
                    props.vocabTrainingOps.duplicatePopup?.training.groupId,
                    props.vocabTrainingOps.keepClassOnDuplicate,
                    props.vocabTrainingOps.toggleKeepClassOnDuplicate
                )}
            </CreateEditTextInputPopup>

            <ToastMessage
                isOpen={props.testOps.shareToast.isOpen}
                onOpenChange={props.testOps.shareToast.onOpenChange}
                text={props.testOps.shareToast.message}
                duration={props.testOps.shareToast.duration}
                variant={props.testOps.shareToast.variant}
            />

            <ToastMessage
                isOpen={props.vocabTrainingOps.shareToast.isOpen}
                onOpenChange={(open) => props.vocabTrainingOps.setShareToast((prev) => ({ ...prev, isOpen: open }))}
                text={props.vocabTrainingOps.shareToast.message}
                variant={props.vocabTrainingOps.shareToast.variant}
                toastKey={props.vocabTrainingOps.shareToast.toastKey}
            />

            {editTestResult && editTestVisibility && (
                <EditActivityVisibilityModal
                    isOpen
                    onClose={props.testOps.handleCloseEditActivityVisibility}
                    initialVisibility={editTestResult.visibility}
                    initialPlannedDate={
                        editTestResult.visibility === ActivityVisibility.PLANNED ? editTestResult.plannedDate : null
                    }
                    deadline={editTestVisibilityDeadline}
                    theme="test"
                    onSave={async ({ visibility, plannedDate }) => {
                        await props.testOps.handleSaveTestVisibility({
                            visibilityDate: visibilityToMs(visibility, plannedDate),
                        });
                    }}
                />
            )}

            {editVocabResult && (
                <EditActivityVisibilityModal
                    isOpen
                    onClose={props.vocabTrainingOps.handleCloseEditActivityVisibility}
                    initialVisibility={editVocabResult.visibility}
                    initialPlannedDate={
                        editVocabResult.visibility === ActivityVisibility.PLANNED ? editVocabResult.plannedDate : null
                    }
                    theme="vocab"
                    onSave={async ({ visibility, plannedDate }) => {
                        await props.vocabTrainingOps.handleSaveVocabVisibility({
                            visibilityDate: toVisibilityDate(visibility, plannedDate),
                        });
                    }}
                />
            )}
        </>
    );
};
